import { Collapse, Flex, Form } from "antd"
import { Icon, Legend, Number, Text, Button } from "components/common"
import { useCallback, useEffect, useMemo, useState } from "react"
import { t } from "i18next"
import { numberProps } from "features/leads/components/addSalesModal/utils/constants"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import { debounce } from "lodash"
import IBANInfo from "features/shared/components/ibanInfo/ibanInfo"
import getPrivilege from "utils/privileges/privileges"
import PaymentItem from "./components/PaymentItem"
import {
  getNextTaskKey,
  getPaymentHeaderSubtitle,
  isPaymentRowComplete,
  paymentProofMatches,
  PAYMENTS_FORM_LIST_NAME,
  validatePaymentsListTotals,
} from "./utils/paymentUtils"

import theme from "utils/themeVars"
import { TitleDescription } from "components/custom"

const MAX_PAYMENTS = 4

const PaymentSection = ({ rules, clientIbanRef }) => {
  const {
    addSalesApis,
    form,
    stateManager,
    formWatchers,
    minTokenDetails,
    isEscrowSelfFinance,
    lastPaymentStatus,
    lastPaymentType,
    client,
    lead,
    showNextPayment,
    vasEnabled,
    vasFees,
  } = useAddSalesContext()

  const { project, unit, cdpCommitment } = formWatchers
  const { leadSalesInfo, taskTypes, saleEvent, getContractExpiry, viban, fetchingVIBAN, clientIban, modeOfPayments } =
    addSalesApis

  const payments = Form.useWatch(PAYMENTS_FORM_LIST_NAME, form) || []
  const [activePanels, setActivePanels] = useState(["0"])

  const debouncedGetContractExpiry = useCallback(
    debounce(params => {
      getContractExpiry(params).unwrap().catch(console.error)
    }, 500),
    []
  )

  const updateNextTask = useCallback(
    (rawValue, tokenDetails) => {
      const key = getNextTaskKey({
        value: +rawValue,
        leadSalesInfo,
        dealPrice: stateManager?.dealPrice,
        cdpCommitment,
        project,
        saleEvent,
        tokenDetails,
        vasEnabled,
        vasFees,
      })
      form.setFieldValue("nextTask", key ? taskTypes?.nextStatuses?.find(item => item.key === key)?.id : null)
    },
    [stateManager?.dealPrice, leadSalesInfo, project, taskTypes, cdpCommitment, vasEnabled, vasFees, saleEvent]
  )

  const onChangeAmount = useCallback(
    (amount, tokenDetails) => {
      form.setFieldValue("deadLine", null)
      updateNextTask(amount, tokenDetails)

      const dealPrice = stateManager?.dealPrice
      if (!(+amount > 0) || !(+dealPrice > 0)) return
      const listingIdForContractExpiry = unit?.listingId ?? leadSalesInfo?.listingId
      if (listingIdForContractExpiry == null ) return 
      debouncedGetContractExpiry({
        listingId: unit?.listingId ?? leadSalesInfo?.listingId,
        params: {
          tokenAmount: amount,
          dealPrice,
          contractListingId: leadSalesInfo?.contracts?.[0]?.contractListingId,
        },
      })
    },
    [stateManager?.dealPrice, leadSalesInfo, unit, updateNextTask, debouncedGetContractExpiry, form]
  )

  const totalEnteredAmount = useMemo(
    () => (payments || []).reduce((sum, item) => sum + (+item?.amount || 0), 0),
    [payments]
  )

  useEffect(() => {
    if (!payments?.length) {
      form.setFieldValue(PAYMENTS_FORM_LIST_NAME, [{}])
      return
    }

    const firstPayment = payments[0] || {}
    const total = totalEnteredAmount
    const mode = firstPayment?.modeOfPayment ?? null
    const proof = firstPayment?.paymentProof ?? null
    const curAmount = form.getFieldValue("amount")
    const curMode = form.getFieldValue("modeOfPayment")
    const curProof = form.getFieldValue("paymentProof")
    if (curAmount !== total || curMode !== mode || !paymentProofMatches(curProof, proof)) {
      form.setFieldsValue({
        amount: total,
        modeOfPayment: mode,
        paymentProof: proof,
      })
    }
  }, [payments, totalEnteredAmount, form])

  useEffect(() => {
    onChangeAmount(totalEnteredAmount, minTokenDetails)
  }, [totalEnteredAmount, stateManager?.dealPrice, onChangeAmount])

  useEffect(() => {
    updateNextTask(totalEnteredAmount, minTokenDetails)
  }, [minTokenDetails, updateNextTask])

  if (leadSalesInfo?.verifiedWon || (!leadSalesInfo?.unitPrice && (!unit || !project))) return null

  const showIBANInfo = isEscrowSelfFinance && !leadSalesInfo?.isFirstSalesTask && lastPaymentStatus !== "rejected"

  return (
    <>
      {!!showIBANInfo && (
        <IBANInfo
          clientId={client?.id}
          clientIbanRef={clientIbanRef}
          vIBAN={viban?.data?.virtualIban}
          contractId={leadSalesInfo?.contracts?.[0]?.contractId}
          clientIBAN={clientIban?.data?.iban}
          disableButton={lastPaymentStatus !== "completed"}
          loading={fetchingVIBAN}
          whatsappButtonDisabled={!getPrivilege("can_view_client_contact", lead?.privileges)}
        />
      )}

      {!!showNextPayment && (
        <div>
          <Legend title={t("Add Payment")} />
          <Form.List name={PAYMENTS_FORM_LIST_NAME}>
            {(fields, { add, remove }) => {
              const lastField = fields[fields.length - 1]
              const lastPaymentRow = lastField != null && payments != null ? payments[lastField.name] : undefined
              return (
                <>
                  <Collapse
                    activeKey={activePanels}
                    onChange={keys => setActivePanels(Array.isArray(keys) ? keys : [keys])}
                    expandIconPosition="end"
                  >
                    {fields.map((field, index) => {
                      const { key, name: listFieldName, ...restField } = field
                      const row = payments?.[field.name]
                      const showCompletedHeader = isPaymentRowComplete(row)
                      const headerSubtitle = showCompletedHeader ? getPaymentHeaderSubtitle(row, modeOfPayments) : ""

                      const panelHeader = showCompletedHeader ? (
                        <TitleDescription
                          titleClassName="bold"
                          title={
                            <Flex align="center" gap="8px">
                              <Number
                                {...numberProps}
                                value={+row.amount}
                                style={{ fontSize: 16, fontWeight: 700 }}
                                className="bold"
                              />
                              <Icon icon="HiCheckCircle" size="1.25em" color={theme["primary-color"]} />
                            </Flex>
                          }
                          description={!!headerSubtitle && <Text type="secondary">{headerSubtitle}</Text>}
                        />
                      ) : (
                        <span>
                          {t("Payment")} # {index + 1}
                          {row?.amount != null && row?.amount !== "" ? (
                            <>
                              {" — "}
                              <Number {...numberProps} value={+row.amount} />
                            </>
                          ) : null}
                        </span>
                      )

                      return (
                        <Collapse.Panel
                          key={key}
                          header={panelHeader}
                          extra={
                            index > 0 ? (
                              <Icon
                                icon="FiTrash2"
                                size="16px"
                                color="#e83a3a"
                                onClick={e => {
                                  e.stopPropagation()
                                  remove(field.name)
                                  setActivePanels(prev => prev.filter(k => k !== String(field.name)))
                                }}
                              />
                            ) : null
                          }
                        >
                          <PaymentItem listFieldName={listFieldName} restField={restField} rules={rules} />
                        </Collapse.Panel>
                      )
                    })}
                  </Collapse>
                  <div>
                    <Button
                      style={{ marginTop: 16 }}
                      text={t("Add Another Payment")}
                      icon="HiPlus"
                      size="large"
                      type="light"
                      color={theme["primary-color"]}
                      disabled={fields.length >= MAX_PAYMENTS || !isPaymentRowComplete(lastPaymentRow)}
                      onClick={() => {
                        const newPanelKey = String(fields.length)
                        add({})
                        setActivePanels([newPanelKey])
                      }}
                    />
                  </div>
                </>
              )
            }}
          </Form.List>
        </div>
      )}
    </>
  )
}

export default PaymentSection
